import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BellIcon, BellOff } from "lucide-react";
import {
  resolveNotificationTitle,
  resolveNotificationTargetRoute,
} from "../utils/notificationUtils";

// Gregorian calendar with Latin digits in Arabic, matching the rest of the app.
const dateLocale = (lang) => (lang === "ar" ? "ar-u-nu-latn" : lang || "en");

const timeAgoLabel = (createdDate, lang, t) => {
  const diffDays = Math.ceil(Math.abs(new Date() - createdDate) / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return t("today") || "Today";
  if (diffDays === 2) return t("yesterday") || "Yesterday";
  if (diffDays <= 7) {
    return (
      t("daysAgoCount", { count: diffDays - 1 }) ||
      `${diffDays - 1} ${t("daysAgo") || "days ago"}`
    );
  }
  return createdDate.toLocaleDateString(dateLocale(lang), { month: "short", day: "numeric" });
};

// Text for notifications that don't carry their own title.
const fallbackText = (notification, t) =>
  [
    t("NotificationTender"),
    notification?.Tender_reference_number,
    "-",
    t("NotificationTenderProject"),
    notification?.TenderName,
    notification.Notification_Type === "deadline reminder"
      ? t("high")
      : t("NotificationTenderOpenForOffer"),
  ].join(" ");

// Bell button with unread badge and its dropdown panel, shared by the desktop and
// mobile headers. "mobile" anchors the panel to the header so it fits narrow screens.
export default function NotificationsMenu({
  open,
  onOpenChange,
  unread = [],
  unreadCount = 0,
  companyId,
  onMarkAsRead,
  variant = "desktop",
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isMobile = variant === "mobile";

  return (
    <div className={`notification-wrapper inline-block ${isMobile ? "" : "relative"}`}>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `${t("notifications")}, ${unreadCount} ${t("unread")}` : t("notifications")}
        className={`group relative flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-200 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 dark:hover:bg-slate-700 ${
          open ? "bg-slate-100 dark:bg-slate-700" : ""
        }`}
      >
        <BellIcon
          size={20}
          strokeWidth={2}
          aria-hidden="true"
          className={`origin-top transition-transform duration-300 group-hover:rotate-12 ${
            open
              ? "text-(--primary-green)"
              : unreadCount > 0
                ? "text-slate-700 dark:text-slate-100"
                : "text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200"
          }`}
        />
        <UnreadBadge count={unreadCount} className="absolute -top-0.5 -end-0.5 ring-2 ring-white dark:ring-slate-800" />
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-2 animate-menu-in overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 motion-reduce:animate-none dark:border-slate-700 dark:bg-slate-800 ${
            isMobile
              ? "start-3 end-3 top-full origin-top sm:start-auto sm:w-80"
              : "end-0 w-80 origin-top-right rtl:origin-top-left"
          }`}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">{t("notifications")}</h2>
            {unreadCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400">
                {unreadCount} {t("unread")}
              </span>
            )}
          </div>
          <div className="h-px bg-slate-100 dark:bg-slate-700" />

          <div className={`overflow-y-auto p-1.5 ${isMobile ? "max-h-[60vh]" : "max-h-80"}`}>
            {unread.length > 0 ? (
              unread.map((notification, index) => {
                const title = resolveNotificationTitle(notification, lang);
                return (
                  <Link
                    to={resolveNotificationTargetRoute(notification, companyId)}
                    key={notification.ID || index}
                    className="flex gap-3 rounded-lg px-2.5 py-2.5 transition-colors duration-150 hover:bg-slate-100 active:bg-slate-100 dark:hover:bg-slate-700 dark:active:bg-slate-700"
                    onClick={() => {
                      onMarkAsRead(notification.ID);
                      onOpenChange(false);
                    }}
                  >
                    <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-(--primary-green)" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm leading-snug text-slate-700 dark:text-slate-200" style={{ whiteSpace: title ? "pre-line" : undefined }}>
                        {title || fallbackText(notification, t)}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {timeAgoLabel(new Date(notification.Creation_Date), lang, t)}
                      </p>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500">
                  <BellOff size={18} strokeWidth={2} aria-hidden="true" />
                </span>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t("noNotifications") || "No active notifications"}
                </p>
              </div>
            )}
          </div>

          <Link
            to="/notifications"
            onClick={() => onOpenChange(false)}
            className="block border-t border-slate-100 px-4 py-3 text-center text-sm font-semibold text-(--primary-green) transition-colors duration-150 hover:bg-slate-50 dark:border-slate-700 dark:text-emerald-400 dark:hover:bg-slate-700/60"
          >
            {t("viewAll")}
          </Link>
        </div>
      )}
    </div>
  );
}

// Red unread count, capped at 99+. Decorative: the count is read out by the parent's label.
export function UnreadBadge({ count, className = "" }) {
  if (!count) return null;
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold tabular-nums text-white ${className}`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
